import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import {
    Button,
    Heading1,
    Stack,
    useNavBar,
} from '@myphx-engineering/phx-design-system';

import { useDrugProgram } from '../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import { GetPrescriberOrderDocument } from '../../../graphql/generated/graphql';
import { usePatientContext } from '../../../hooks/use-patient-context';
import { getNdcDefinition } from '../../../util/config/get-ndc-definition';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { Loader } from '../../common/Loader';
import { SwitchPrescriptionHeader } from '../../common/SwitchPrescriptionHeader';

import { VerifySwitchPrescriptionDetails } from './VerifySwitchPrescriptionDetails';

export const VerifySwitchPrescriptionPage = () => {
    const { prescriberOrderId } = useParams();
    const { patient } = usePatientContext();
    const { t } = useTranslation();
    const drugProgram = useDrugProgram();
    const navigate = useNavigate();
    const { setBackOverrideState } = useNavBar();

    useEffect(() => {
        if (!prescriberOrderId) return;

        const backToOrderDetails = () => {
            navigate(
                getAbsoluteRoute('order.prescriberOrderId.details', {
                    prescriberOrderId,
                })
            );
        };

        setBackOverrideState({ overrideFn: backToOrderDetails });

        return () => {
            setBackOverrideState({ overrideFn: null });
        };
    }, [navigate, prescriberOrderId, setBackOverrideState]);

    const { data, loading } = useQuery(GetPrescriberOrderDocument, {
        variables: { prescriberOrderId: prescriberOrderId ?? '' },
        skip: !prescriberOrderId,
    });

    if (!prescriberOrderId || !patient) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    if (loading) {
        return <Loader />;
    }

    const prescriberOrder = getFragment(data?.prescriberOrderV2);

    if (!prescriberOrder) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    if (prescriberOrder.__typename !== 'DigitallyReceivedPrescriberOrder') {
        return (
            <Navigate
                to={getAbsoluteRoute('order.prescriberOrderId.verifyPatient', {
                    prescriberOrderId,
                })}
            />
        );
    }
    const medication = getFragment(prescriberOrder.authorizedMedication);

    if (!prescriberOrder || !medication) {
        return <Navigate to={getAbsoluteRoute('notFound')} />;
    }

    const ndcDefinition = getNdcDefinition(drugProgram, medication);
    const switchedMedicationNdc =
        ndcDefinition?.alternateConfigs[0].alternateNdc;
    if (!switchedMedicationNdc) {
        return <Navigate to={getAbsoluteRoute('notFound')} />;
    }
    const providerPhoneNumber =
        getFragment(prescriberOrder.prescriber).phoneNumber ?? '';

    const handleClick = async () => {
        location.href = `tel:${providerPhoneNumber}`;
    };

    return (
        <Stack>
            <Heading1>{t('verifySwitchPage.heading')}</Heading1>

            <SwitchPrescriptionHeader
                switchedMedicationId={switchedMedicationNdc}
                originalQuantity={prescriberOrder.quantity}
            />

            <VerifySwitchPrescriptionDetails
                switchedMedicationId={switchedMedicationNdc}
            />

            <Button onClick={handleClick} size="lg">
                {t('verifySwitchPage.button')}
            </Button>
        </Stack>
    );
};
